// Chemistry → Section 7: The Earth and the Atmosphere (KS3, sourced from
// the Grade9Ready science PDF). Same rich-content template as the other
// section files.
import authored from './authoredTopics.json';

const AUTHORED_KEY = 'chemistry/earth-and-atmosphere';
const AUTHORED = (authored && authored[AUTHORED_KEY]) || [];
const AUTHORED_BY_ID = Object.fromEntries(AUTHORED.map((t) => [t.id, t]));

// =====================================================================
// 7.1  THE EARTH'S STRUCTURE
// =====================================================================
const STRUCTURE_TOPIC = {
  id: 'chem-7.1',
  title: "The Earth's Structure",
  slug: 'earths-structure',
  pageRef: 'Section 7 · Topic 1',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'The four layers',
        body:
          "Earth has four main layers. The crust is the thin rocky outer shell where we live (5–70 km thick). The mantle is the thick, hot, semi-molten layer beneath — it slowly flows over millions of years. The outer core is liquid iron and nickel. The inner core is solid iron and nickel under enormous pressure. The deeper you go, the hotter it gets — the inner core is around 5500 °C, hotter than the Sun's surface.",
        check: {
          question: 'Which layer of Earth is liquid?',
          options: ['Crust', 'Mantle', 'Outer core', 'Inner core'],
          answer: 'Outer core',
          explainer: 'The outer core is molten iron and nickel; the inner core is solid because of the huge pressure.',
        },
      },
      {
        heading: 'Tectonic plates',
        body:
          'The crust is broken into giant pieces called tectonic plates. They float on the semi-molten mantle and move very slowly (a few cm per year — about as fast as your fingernails grow). Where plates meet, they can pull apart, push together, or slide past each other. These movements cause earthquakes, volcanoes and mountain building.',
        check: {
          question: 'What floats on top of the mantle and slowly moves?',
          options: ['The atmosphere', 'Tectonic plates', 'The inner core', 'The Moon'],
          answer: 'Tectonic plates',
          explainer: 'Tectonic plates are giant pieces of crust drifting on the mantle.',
        },
      },
      {
        heading: 'How we know',
        body:
          "Nobody has drilled all the way through the crust. Scientists work out what's inside by studying earthquakes — different waves travel through solids and liquids at different speeds, so the way they reach far-away detectors reveals the layers. Magnetic measurements show the core is iron-rich (the spinning iron is what gives Earth its magnetic field).",
        check: {
          question: 'How do scientists work out what Earth is made of inside?',
          options: ['By drilling all the way down', 'By measuring earthquakes (seismic waves)', 'By X-rays from space', 'By guessing'],
          answer: 'By measuring earthquakes (seismic waves)',
          explainer: "Different waves behave differently in solid vs. liquid layers, so they reveal Earth's structure.",
        },
      },
    ],
    keyPoints: [
      'Four layers: crust, mantle, outer core, inner core',
      'Crust = solid + thin (5–70 km)',
      'Mantle = hot semi-molten rock that slowly flows',
      'Outer core = liquid iron/nickel; inner core = solid iron/nickel',
      'Tectonic plates float on the mantle, move ~cm per year',
      'Earthquakes, volcanoes and mountains form at plate boundaries',
    ],
    workedExamples: [
      {
        problem: 'A volcano erupts hot liquid rock. From which layer of Earth has the liquid rock come, and why is it so hot?',
        steps: [
          { step: 1, instruction: 'Identify source', working: 'Liquid rock (magma) comes from the mantle / upper mantle' },
          { step: 2, instruction: 'Why hot?', working: 'Heat from the core radiates upwards; pressure plus radioactive decay keeps the mantle around 1000–3500 °C' },
        ],
        answer: 'From the (upper) mantle. The mantle is heated by the core below and by radioactive decay of elements in the rock.',
        commonMistake: 'Saying the lava comes from the core — the core is far too deep and contains iron, not the rocky magma seen in lava.',
      },
    ],
    whyItWorks:
      "Earth's layers form because denser materials sank when the planet was molten and lighter rocks floated to the top. The metallic core gives us our magnetic field; the mobile mantle drives plate tectonics; the crust is where life happens.",
  },
  glossary: [
    { term: 'Crust', definition: "Earth's thin rocky outer layer." },
    { term: 'Mantle', definition: 'The hot, semi-molten layer beneath the crust that slowly flows.' },
    { term: 'Outer core', definition: 'The liquid layer of iron and nickel below the mantle.' },
    { term: 'Inner core', definition: 'The solid central ball of iron and nickel — kept solid by extreme pressure.' },
    { term: 'Tectonic plates', definition: 'Giant pieces of crust that move slowly on the mantle.' },
    { term: 'Magma', definition: 'Hot liquid rock from the mantle.' },
  ],
  examQuestion: {
    prompt:
      "(a) Name the four main layers of the Earth in order from outside to inside. (b) State the difference between the inner and outer core. (c) Suggest why earthquakes mostly happen at the edges of tectonic plates. (5 marks)",
    maxMarks: 5,
    markSchemePoints: [
      'Crust, mantle, outer core, inner core (in order)',
      'Outer core is liquid; inner core is solid',
      "(Inner core is solid because of extreme pressure even at high temperature)",
      'Edges of plates push, pull or slide past each other',
      'This causes sudden movements which release energy as earthquakes',
    ],
  },
  practice: {
    warmUp: [
      { id: 'chem-7.1.w1', type: 'text', question: 'The thin solid outer layer of Earth is called the ___ .', answer: 'crust', hint1: '5 letters', hint2: 'Where we live', hint3: 'Starts with c' },
      { id: 'chem-7.1.w2', type: 'choice', question: 'Which layer is liquid?', options: ['Crust', 'Mantle', 'Outer core', 'Inner core'], answer: 'Outer core', hint1: 'Two words', hint2: 'Iron + nickel', hint3: 'Below the mantle' },
      { id: 'chem-7.1.w3', type: 'choice', question: 'Tectonic plates float on the…', options: ['Inner core', 'Atmosphere', 'Mantle', 'Outer core'], answer: 'Mantle', hint1: 'Hot semi-molten rock', hint2: '6 letters', hint3: 'Just below crust' },
    ],
    level1: [
      { id: 'chem-7.1.1', type: 'choice', question: 'Roughly how fast do tectonic plates move?', options: ['Several metres per second', 'A few cm per year', 'Hundreds of km per day', 'Once per century'], answer: 'A few cm per year', hint1: 'Very slow', hint2: 'Like fingernail growth', hint3: 'Centimetres' },
      { id: 'chem-7.1.2', type: 'text', question: 'Hot liquid rock that comes out of a volcano (when underground) is called ___ .', answer: 'magma', hint1: '5 letters', hint2: 'Becomes lava above ground', hint3: 'Starts with m' },
    ],
    level2: [
      { id: 'chem-7.1.3', type: 'choice', question: 'Why is the inner core solid even though it is the hottest layer?', options: ['It is cold', 'There is huge pressure squeezing it solid', 'It is small', 'It contains plastic'], answer: 'There is huge pressure squeezing it solid', hint1: 'Pressure beats temperature', hint2: 'Squeezed atoms', hint3: 'Metal under load' },
      { id: 'chem-7.1.4', type: 'choice', question: 'Earthquake (seismic) waves help scientists work out…', options: ['Air pressure', "The internal structure of the Earth", 'The orbit of the Moon', 'The age of the Sun'], answer: "The internal structure of the Earth", hint1: 'Waves change with material', hint2: 'Reveal layers', hint3: 'Internal mapping' },
    ],
    level3: [
      { id: 'chem-7.1.5', type: 'choice', question: "Which TWO metals make up most of Earth's core?", options: ['Iron and nickel', 'Iron and copper', 'Gold and silver', 'Aluminium and tin'], answer: 'Iron and nickel', hint1: 'Magnetic metals', hint2: 'Two transition metals', hint3: 'Iron + Ni' },
      { id: 'chem-7.1.6', type: 'text', question: 'Pieces of crust that move slowly on the mantle are called tectonic ___ .', answer: 'plates', hint1: '6 letters', hint2: 'Continental jigsaw', hint3: 'Plural' },
    ],
  },
};

// =====================================================================
// 7.2  ROCK TYPES
// =====================================================================
const ROCK_TYPES_TOPIC = {
  id: 'chem-7.2',
  title: 'Rock Types',
  slug: 'rock-types',
  pageRef: 'Section 7 · Topic 2',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Three rock families',
        body:
          'All rocks fall into three groups based on how they formed. Igneous rocks form when molten rock (magma or lava) cools and hardens. Sedimentary rocks form when layers of sediment (mud, sand, shells) are squashed together over millions of years. Metamorphic rocks form when existing rocks are changed by heat and/or pressure deep in the crust.',
        check: {
          question: 'Which rock type is made when magma or lava cools?',
          options: ['Igneous', 'Sedimentary', 'Metamorphic', 'Mineral'],
          answer: 'Igneous',
          explainer: '"Igneous" comes from the Latin for fire — these rocks freeze from a molten state.',
        },
      },
      {
        heading: 'Igneous up close',
        body:
          'Igneous rocks are made of interlocking crystals — no layers, no fossils. If the magma cools FAST (e.g. lava on the surface), crystals are tiny — basalt is a classic example. If magma cools SLOWLY underground, crystals are big — granite is a textbook example. Granite is hard, lasts ages, and is used for kitchen worktops and old buildings.',
        check: {
          question: 'Why are granite crystals larger than basalt crystals?',
          options: ['Granite is older', 'Granite cooled slowly underground', 'Basalt is heated more', 'It is random'],
          answer: 'Granite cooled slowly underground',
          explainer: 'Slow cooling gives crystals plenty of time to grow large.',
        },
      },
      {
        heading: 'Sedimentary and metamorphic up close',
        body:
          'Sedimentary rocks: layers (called strata), sometimes with fossils (e.g. limestone, sandstone, chalk, mudstone). Soft compared to igneous. Made under water or wind, then squashed and cemented. Metamorphic rocks: dense, often layered "banding". Examples: limestone → marble (smooth and polished, used in statues); shale/mudstone → slate (used for roofs); sandstone → quartzite. The original rock is changed without melting.',
        check: {
          question: 'Marble is metamorphic. Which rock did it start as?',
          options: ['Granite', 'Basalt', 'Limestone', 'Slate'],
          answer: 'Limestone',
          explainer: 'Heat and pressure recrystallise the calcium carbonate in limestone into marble.',
        },
      },
    ],
    keyPoints: [
      'Igneous: cooled from magma/lava — interlocking crystals',
      'Granite = slow cool (big crystals); basalt = fast cool (small)',
      'Sedimentary: layers of squashed sediment, often with fossils',
      'Examples: limestone, sandstone, chalk',
      'Metamorphic: existing rock changed by heat and/or pressure',
      'Limestone → marble; shale/mudstone → slate',
    ],
    workedExamples: [
      {
        problem: 'A pupil has 3 rock samples: (i) a hard rock made of large interlocking crystals, (ii) a striped rock with parallel layers and a fossil shell, (iii) a smooth, dense polished rock used in statues. Identify the rock TYPE of each.',
        steps: [
          { step: 1, instruction: '(i)', working: 'Large interlocking crystals → cooled slowly → igneous (likely granite)' },
          { step: 2, instruction: '(ii)', working: 'Layers + fossil → laid down underwater → sedimentary' },
          { step: 3, instruction: '(iii)', working: 'Smooth, dense, used for sculpture → metamorphic (likely marble from limestone)' },
        ],
        answer: '(i) igneous, (ii) sedimentary, (iii) metamorphic.',
        commonMistake: "Calling a rock 'metamorphic' just because it looks fancy. The defining feature is being CHANGED from another rock by heat/pressure.",
      },
    ],
    whyItWorks:
      'Rocks are clues to the past. The crystal size of an igneous rock tells you how fast it cooled. Fossils in sedimentary rocks reveal what life looked like millions of years ago. Metamorphic rocks tell us about the heat and pressure deep in the crust.',
  },
  glossary: [
    { term: 'Igneous', definition: 'Rock made by cooling and solidifying magma or lava.' },
    { term: 'Sedimentary', definition: 'Rock made of compressed layers of sediment (sand, mud, shells).' },
    { term: 'Metamorphic', definition: 'Rock made when existing rock is changed by heat and/or pressure.' },
    { term: 'Magma', definition: 'Hot liquid rock underground.' },
    { term: 'Lava', definition: 'Hot liquid rock that has reached the surface.' },
    { term: 'Strata', definition: 'Layers in a sedimentary rock.' },
  ],
  examQuestion: {
    prompt:
      "(a) State the three rock types and how each forms. (b) Explain why granite has bigger crystals than basalt. (c) Suggest a reason fossils are usually found in SEDIMENTARY rocks, not igneous ones. (5 marks)",
    maxMarks: 5,
    markSchemePoints: [
      'Igneous = magma/lava cools and solidifies; Sedimentary = squashed layers of sediment; Metamorphic = changed by heat/pressure',
      'Granite cooled slowly underground',
      'so crystals had more time to grow large',
      'Sedimentary rocks form from sand/mud/shells in water',
      'Igneous rocks form from molten rock — the heat would destroy any fossil remains',
    ],
  },
  practice: {
    warmUp: [
      { id: 'chem-7.2.w1', type: 'text', question: 'Rocks made when magma cools are called ___ rocks.', answer: 'igneous', hint1: '7 letters', hint2: 'Latin for fire', hint3: 'Starts with i' },
      { id: 'chem-7.2.w2', type: 'choice', question: 'Limestone is what type of rock?', options: ['Igneous', 'Sedimentary', 'Metamorphic', 'Mineral'], answer: 'Sedimentary', hint1: 'Layered, often with fossils', hint2: 'Squashed shells', hint3: '11 letters' },
      { id: 'chem-7.2.w3', type: 'choice', question: 'Marble started life as which rock?', options: ['Granite', 'Basalt', 'Limestone', 'Slate'], answer: 'Limestone', hint1: 'Sedimentary parent', hint2: '9 letters', hint3: 'Calcium carbonate' },
    ],
    level1: [
      { id: 'chem-7.2.1', type: 'choice', question: 'Why does granite have BIG crystals?', options: ['It is old', 'It cooled slowly underground', 'It cooled fast on the surface', 'It is metamorphic'], answer: 'It cooled slowly underground', hint1: 'More time to grow', hint2: 'Crystal-time link', hint3: 'Slow = large' },
      { id: 'chem-7.2.2', type: 'text', question: 'Layers in a sedimentary rock are called ___ .', answer: 'strata', hint1: '6 letters', hint2: 'Plural Latin', hint3: 'Geological term' },
    ],
    level2: [
      { id: 'chem-7.2.3', type: 'choice', question: 'Slate is metamorphic. Which rock did it start from?', options: ['Granite', 'Mudstone (shale)', 'Marble', 'Basalt'], answer: 'Mudstone (shale)', hint1: 'Sedimentary parent', hint2: 'Soft layered rock', hint3: 'Fine grain' },
      { id: 'chem-7.2.4', type: 'choice', question: 'Why are fossils usually only found in sedimentary rocks?', options: ['They prefer that colour', 'Sediment buries dead things gently — heat of magma destroys remains', 'Fossils only form near water', 'They are random'], answer: 'Sediment buries dead things gently — heat of magma destroys remains', hint1: 'Igneous = heat = destruction', hint2: 'Sedimentary = preservation', hint3: 'Slow burial' },
    ],
    level3: [
      { id: 'chem-7.2.5', type: 'choice', question: 'Basalt has small crystals because…', options: ['It cooled rapidly above ground (lava)', 'It cooled slowly underground', 'It is metamorphic', 'It is volcanic glass only'], answer: 'It cooled rapidly above ground (lava)', hint1: 'Fast cooling', hint2: 'Lava on surface', hint3: 'Small crystals' },
      { id: 'chem-7.2.6', type: 'text', question: 'Heat and pressure change rocks into ___ rocks (without melting them).', answer: 'metamorphic', hint1: '11 letters', hint2: 'Greek for "shape change"', hint3: 'Starts with m' },
    ],
  },
};

// =====================================================================
// 7.3  THE ROCK CYCLE
// =====================================================================
const ROCK_CYCLE_TOPIC = {
  id: 'chem-7.3',
  title: 'The Rock Cycle',
  slug: 'rock-cycle',
  pageRef: 'Section 7 · Topic 3',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'A rock is never finished',
        body:
          'The rock cycle shows how the three rock types are constantly being recycled into each other over millions of years. Igneous rocks weather into sediment → squash into sedimentary rocks → heat/pressure changes them into metamorphic → eventually melt back into magma → cool to igneous again. No rock lasts forever; every rock has a story.',
        check: {
          question: 'In the rock cycle, what melts back into magma?',
          options: ['Only igneous rocks', 'Only sedimentary rocks', 'Any rock taken deep enough', 'Only metamorphic rocks'],
          answer: 'Any rock taken deep enough',
          explainer: 'Once any rock is dragged deep into the mantle, it can melt and become magma again.',
        },
      },
      {
        heading: 'Weathering, erosion and transport',
        body:
          'Weathering breaks rocks down where they sit (frost cracks them; rain dissolves limestone; tree roots wedge them apart). Erosion moves the broken bits away — wind, rivers and glaciers all do this. Transport carries those particles to a new place (often the sea), where they settle as sediment. Over time, layers compress and cement together — making sedimentary rock.',
        check: {
          question: 'What is the difference between weathering and erosion?',
          options: ['They mean the same thing', 'Weathering breaks rock, erosion moves it', 'Weathering moves rock, erosion breaks it', 'Both happen only at sea'],
          answer: 'Weathering breaks rock, erosion moves it',
          explainer: 'Weathering = break-down on the spot; erosion = transport away.',
        },
      },
      {
        heading: 'Heat, pressure and melting',
        body:
          'Sediment that gets buried deep enough is heated and squashed — it can become metamorphic rock without melting. Push it deeper still, or near a magma chamber, and it melts to magma. Volcanoes spew that magma back to the surface, cool to igneous rock, weather again — and the cycle continues.',
        check: {
          question: 'A sedimentary rock is buried deep AND squashed by huge pressure. It becomes…',
          options: ['Magma', 'Metamorphic rock', 'Sediment', 'Lava'],
          answer: 'Metamorphic rock',
          explainer: 'Heat + pressure (without melting) = metamorphism.',
        },
      },
    ],
    keyPoints: [
      'Rocks are constantly recycled over millions of years',
      'Weathering = break-down on the spot',
      'Erosion = moving the broken pieces',
      'Transport + deposition → sediment → sedimentary rock',
      'Heat + pressure → metamorphic rock',
      'Deep enough → melts to magma → cools → igneous rock',
    ],
    workedExamples: [
      {
        problem: 'A piece of granite is broken down by wind and frost over millions of years. Trace its possible journey through the rock cycle until it could become granite again.',
        steps: [
          { step: 1, instruction: 'Granite weathers', working: 'Wind/frost break it into small grains' },
          { step: 2, instruction: 'Eroded + transported', working: 'River carries the grains to the sea' },
          { step: 3, instruction: 'Deposition', working: 'Layers settle on the sea floor' },
          { step: 4, instruction: 'Compaction', working: 'Squashed + cemented → sandstone (sedimentary)' },
          { step: 5, instruction: 'Buried deeper', working: 'Heat + pressure → quartzite (metamorphic)' },
          { step: 6, instruction: 'Even deeper', working: 'Rock melts → magma → cools slowly → granite again!' },
        ],
        answer: 'Granite → grains → sediment → sandstone → quartzite → magma → granite. Full circle.',
        commonMistake: "Thinking the rock cycle is fast — it takes millions of years for one full loop.",
      },
    ],
    whyItWorks:
      'The rock cycle is driven by Earth\u2019s energy: heat from the core (mantle convection, volcanism) and energy from the Sun (weathering, erosion). The two together keep the surface constantly changing.',
  },
  glossary: [
    { term: 'Weathering', definition: 'Breaking rocks down where they sit, by wind, rain, frost, or roots.' },
    { term: 'Erosion', definition: 'Moving the broken-down pieces away by wind, water or ice.' },
    { term: 'Deposition', definition: 'When sediment settles out of water or wind into layers.' },
    { term: 'Compaction', definition: 'Squashing of sediment into sedimentary rock under the weight of layers above.' },
    { term: 'Metamorphism', definition: 'The change of rock by heat and/or pressure without melting.' },
  ],
  examQuestion: {
    prompt:
      "(a) Define weathering AND erosion. (b) Describe how a sedimentary rock can become a metamorphic rock. (c) Explain why no rock on Earth's surface stays the same forever. (5 marks)",
    maxMarks: 5,
    markSchemePoints: [
      'Weathering = breaking down rock where it sits',
      'Erosion = moving broken pieces away',
      'Sedimentary rock buried deep → heat AND pressure',
      'Without melting, rock recrystallises → metamorphic',
      'Earth\u2019s heat (core) and Sun\u2019s energy (surface) constantly drive the cycle',
    ],
  },
  practice: {
    warmUp: [
      { id: 'chem-7.3.w1', type: 'text', question: 'Breaking rocks down where they sit is called ___ .', answer: 'weathering', hint1: '10 letters', hint2: 'Frost, rain, roots', hint3: 'On-the-spot break-down' },
      { id: 'chem-7.3.w2', type: 'choice', question: 'Sediment squashed into rock is called…', options: ['Igneous', 'Sedimentary', 'Metamorphic', 'Magma'], answer: 'Sedimentary', hint1: 'Layered', hint2: '11 letters', hint3: 'Compaction product' },
      { id: 'chem-7.3.w3', type: 'choice', question: 'Hot liquid rock under the ground is…', options: ['Lava', 'Magma', 'Slag', 'Gravel'], answer: 'Magma', hint1: '5 letters', hint2: 'Underground form', hint3: 'Becomes lava if reaches surface' },
    ],
    level1: [
      { id: 'chem-7.3.1', type: 'choice', question: 'A piece of granite weathers into sand. The sand is later squashed into rock. The new rock is…', options: ['Igneous', 'Sedimentary', 'Metamorphic', 'Magma'], answer: 'Sedimentary', hint1: 'Compacted layers', hint2: 'Sandstone', hint3: 'Layered rock' },
      { id: 'chem-7.3.2', type: 'text', question: 'Sediment is moved by wind, rivers and glaciers. This is called ___ .', answer: 'erosion', hint1: '7 letters', hint2: 'Movement of pieces', hint3: 'Starts with e' },
    ],
    level2: [
      { id: 'chem-7.3.3', type: 'choice', question: 'A rock has been heated and squashed but did NOT melt. It is now…', options: ['Sedimentary', 'Igneous', 'Metamorphic', 'Sediment'], answer: 'Metamorphic', hint1: 'No melting → not igneous', hint2: 'Heat + pressure', hint3: 'Greek shape-change' },
      { id: 'chem-7.3.4', type: 'choice', question: 'Why does the rock cycle never end?', options: ['Earth keeps making new atoms', "Earth's internal heat + Sun's energy keep driving it", 'Magic', 'Plate tectonics ended'], answer: "Earth's internal heat + Sun's energy keep driving it", hint1: 'Two energy sources', hint2: 'Internal + external', hint3: 'Continuous power' },
    ],
    level3: [
      { id: 'chem-7.3.5', type: 'choice', question: 'A sedimentary rock is dragged deep into the mantle and melts. It becomes…', options: ['Sand', 'Magma', 'Lava', 'Soil'], answer: 'Magma', hint1: 'Hot liquid rock', hint2: '5 letters', hint3: 'Underground' },
      { id: 'chem-7.3.6', type: 'text', question: 'Sediment settling out of water or wind into layers is called ___ .', answer: 'deposition', hint1: '10 letters', hint2: 'Sediment laid down', hint3: 'Starts with d' },
    ],
  },
};

// =====================================================================
// 7.4  RECYCLING
// =====================================================================
const RECYCLING_TOPIC = {
  id: 'chem-7.4',
  title: 'Recycling',
  slug: 'recycling',
  pageRef: 'Section 7 · Topic 4',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Why recycle?',
        body:
          'Earth\u2019s minerals and metals are FINITE — once a mineable ore is gone, that\u2019s it. Recycling means reusing materials (glass, paper, plastic, metal) instead of throwing them away. It saves raw materials, saves energy, reduces pollution, and reduces the amount of rubbish going to landfill. Each tonne of recycled aluminium uses around 95% LESS energy than making aluminium from fresh ore.',
        check: {
          question: 'Recycling aluminium uses about ___ less energy than making it from ore.',
          options: ['5%', '20%', '60%', '95%'],
          answer: '95%',
          explainer: 'Recycling is the difference between melting metal once and extracting it from rock — huge energy saving.',
        },
      },
      {
        heading: 'How recycling works for metals',
        body:
          'Used metal items (cans, scrap cars, old wiring) are collected and sorted (often using magnets to pick out iron/steel). The metal is shredded, melted in a furnace, then poured into ingots ready to make new products. The same atoms are reused — that\u2019s why aluminium drink cans you put in the recycling bin can be back on a shelf as new cans within 6 weeks.',
        check: {
          question: 'How is iron usually separated from a mixed-metal recycling pile?',
          options: ['By colour', 'With a magnet', 'By weight only', 'By smell'],
          answer: 'With a magnet',
          explainer: 'Iron and steel are magnetic; aluminium isn\u2019t. A simple magnetic belt sorts them.',
        },
      },
      {
        heading: 'Other materials',
        body:
          'Glass: crushed and re-melted. Paper: pulped, cleaned and pressed into new sheets — but each cycle shortens the fibres, so paper can only be recycled around 5–7 times. Plastic: sorted by type (look for the ♻ number), then cleaned, shredded and re-melted. Some plastics are difficult to recycle which is why "reduce" and "reuse" come BEFORE "recycle".',
        check: {
          question: 'Which order matches the "waste hierarchy"?',
          options: ['Recycle → Reuse → Reduce', 'Reduce → Reuse → Recycle', 'Reuse → Recycle → Reduce', 'Reduce → Recycle → Reuse'],
          answer: 'Reduce → Reuse → Recycle',
          explainer: "Use less first; reuse what you have; recycle what's left.",
        },
      },
    ],
    keyPoints: [
      'Earth\u2019s metal/mineral ores are finite',
      'Recycling saves raw materials, energy, and landfill space',
      'Recycling aluminium = ~95% energy saving',
      'Magnets sort iron/steel from non-magnetic metals',
      'Paper can only be recycled 5–7 times (fibres shorten)',
      'Best order: Reduce → Reuse → Recycle',
    ],
    workedExamples: [
      {
        problem: 'A council collects mixed cans (steel + aluminium). Suggest a step-by-step recycling process.',
        steps: [
          { step: 1, instruction: 'Sort', working: 'Pass cans under a magnetic belt — steel cans are pulled aside' },
          { step: 2, instruction: 'Clean', working: 'Wash off labels, food residue' },
          { step: 3, instruction: 'Shred + melt', working: 'Each metal is shredded and melted in a separate furnace' },
          { step: 4, instruction: 'Cast', working: 'Pour molten metal into ingots' },
          { step: 5, instruction: 'Reuse', working: 'Roll into new sheets → make new cans' },
        ],
        answer: 'Magnetic separation → cleaning → shred + melt → cast → re-roll into new cans.',
        commonMistake: 'Forgetting to separate the metals first — they have different melting points and cannot be melted together.',
      },
    ],
    whyItWorks:
      "Recycling is essentially a shortcut around the rock cycle. Instead of waiting millions of years for new metal ore to form, we just reuse the metal that's already been refined. It's good for the planet AND much cheaper.",
  },
  glossary: [
    { term: 'Recycling', definition: 'Reprocessing used materials so they can be used again.' },
    { term: 'Finite resource', definition: 'A resource that exists in limited amounts and will eventually run out.' },
    { term: 'Ore', definition: 'A rock containing enough metal to be worth extracting.' },
    { term: 'Landfill', definition: 'A site where rubbish is buried.' },
    { term: 'Waste hierarchy', definition: 'Reduce → Reuse → Recycle — the preferred order for handling waste.' },
  ],
  examQuestion: {
    prompt:
      '(a) Give THREE benefits of recycling metals. (b) Describe how iron is separated from aluminium in a recycling plant. (c) Suggest one reason why "reuse" is better than "recycle". (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Saves raw materials / saves energy / reduces landfill (any 3, including pollution reduction or money saved)',
      'Iron is magnetic; magnets pick it out of the mixed scrap',
      'Aluminium is non-magnetic so passes through',
      'Reuse uses NO extra energy to reprocess',
      'Reuse keeps the item working for longer / produces no extra waste in the meantime',
    ],
  },
  practice: {
    warmUp: [
      { id: 'chem-7.4.w1', type: 'text', question: 'Reusing materials to make new products is called ___ .', answer: 'recycling', hint1: '9 letters', hint2: 'Topic title', hint3: 'Starts with r' },
      { id: 'chem-7.4.w2', type: 'choice', question: 'Best order for waste handling?', options: ['Recycle → Reuse → Reduce', 'Reduce → Reuse → Recycle', 'Reuse → Reduce → Recycle', 'Reduce → Recycle → Reuse'], answer: 'Reduce → Reuse → Recycle', hint1: 'Use less first', hint2: 'Then reuse', hint3: 'Recycle as last' },
      { id: 'chem-7.4.w3', type: 'choice', question: 'Magnets at recycling plants pick out…', options: ['Aluminium', 'Iron and steel', 'Plastic', 'Glass'], answer: 'Iron and steel', hint1: 'Magnetic metals', hint2: 'Two examples', hint3: 'Most common scrap' },
    ],
    level1: [
      { id: 'chem-7.4.1', type: 'number', question: 'Recycling aluminium saves about how much energy (compared to fresh ore, in %)?', answer: '95', hint1: 'Two digits', hint2: 'Almost all of it', hint3: 'Around 95%' },
      { id: 'chem-7.4.2', type: 'text', question: 'A rock containing enough metal to be worth extracting is called an ___ .', answer: 'ore', hint1: '3 letters', hint2: 'Mining target', hint3: 'Starts with o' },
    ],
    level2: [
      { id: 'chem-7.4.3', type: 'choice', question: 'Why can paper only be recycled around 5–7 times?', options: ['It runs out of ink', 'The fibres get shorter each time', 'It changes colour', 'It absorbs water'], answer: 'The fibres get shorter each time', hint1: 'Mechanical limit', hint2: 'Strength reduces', hint3: 'Eventually too short to bind' },
      { id: 'chem-7.4.4', type: 'choice', question: 'Which is a "finite" resource?', options: ['Sunlight', 'Wind', 'Iron ore', 'Tides'], answer: 'Iron ore', hint1: 'Will run out', hint2: 'Mined from rocks', hint3: 'Limited supply' },
    ],
    level3: [
      { id: 'chem-7.4.5', type: 'choice', question: 'A glass bottle is reused (refilled) instead of recycled. Why is this BETTER?', options: ['It looks nicer', 'It uses no extra melting energy', 'Glass is rare', 'It is faster'], answer: 'It uses no extra melting energy', hint1: 'No re-melt step', hint2: 'No new processing', hint3: 'Energy saving' },
      { id: 'chem-7.4.6', type: 'text', question: 'Buried rubbish ends up in a ___ site.', answer: 'landfill', hint1: '8 letters', hint2: 'Big hole in the ground', hint3: 'Starts with l' },
    ],
  },
};

// =====================================================================
// 7.5  THE CARBON CYCLE
// =====================================================================
const CARBON_CYCLE_TOPIC = {
  id: 'chem-7.5',
  title: 'The Carbon Cycle',
  slug: 'carbon-cycle',
  pageRef: 'Section 7 · Topic 5',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'The big idea',
        body:
          "Carbon atoms move continually between the air, living things, oceans and rocks. The four key processes: (1) Photosynthesis takes CO₂ OUT of the air and locks it into plants. (2) Respiration releases CO₂ from plants and animals back into the air. (3) Combustion (burning fuels) also releases CO₂. (4) Decomposition (when dead things rot) releases CO₂ as well. In a balanced cycle, the amount IN equals the amount OUT, keeping atmospheric CO₂ steady.",
        check: {
          question: 'Which process REMOVES CO₂ from the air?',
          options: ['Respiration', 'Combustion', 'Photosynthesis', 'Decomposition'],
          answer: 'Photosynthesis',
          explainer: 'Plants suck in CO₂ + water + sunlight → glucose + oxygen. Carbon is now locked in the plant.',
        },
      },
      {
        heading: 'Long-term carbon storage',
        body:
          'Some carbon gets locked away for millions of years. Animals build calcium carbonate shells; when they die, the shells settle on the sea floor and eventually become limestone — a HUGE carbon sink. Dead plants buried under mud over millions of years can become coal, oil and gas (fossil fuels). When humans burn those fuels, that carbon — locked away for hundreds of millions of years — is released back into the atmosphere very quickly.',
        check: {
          question: 'Limestone is a long-term store of carbon. It formed from…',
          options: ['Plants', 'Animal shells', 'Volcanoes', 'Soil'],
          answer: 'Animal shells',
          explainer: 'Calcium carbonate shells settle and compress over millions of years into limestone.',
        },
      },
      {
        heading: 'Why humans matter',
        body:
          'Burning fossil fuels releases extra CO₂ that hasn\u2019t been part of the recent cycle for ages. Cutting down forests (deforestation) means there are fewer plants doing photosynthesis, so less CO₂ is being removed. The result: atmospheric CO₂ has risen sharply since 1850. More CO₂ traps more heat → climate change (covered in 7.6).',
        check: {
          question: 'Which human activity adds CO₂ to the atmosphere?',
          options: ['Photosynthesis', 'Burning fossil fuels', 'Eating salad', 'Sleeping'],
          answer: 'Burning fossil fuels',
          explainer: 'Burning coal, oil and gas releases CO₂ stored over millions of years.',
        },
      },
    ],
    keyPoints: [
      'Photosynthesis = CO₂ OUT of air',
      'Respiration, combustion, decomposition = CO₂ INTO air',
      'In balance: atmospheric CO₂ stays roughly steady',
      'Long-term stores: limestone (shells), fossil fuels (dead plants/plankton)',
      'Burning fossil fuels + deforestation = more CO₂',
      'More CO₂ → more warming → climate change',
    ],
    workedExamples: [
      {
        problem: 'For each, name the carbon-cycle process and whether it adds or removes CO₂ from the air. (i) A tree photosynthesising. (ii) A mouse breathing out. (iii) A fungus rotting a log. (iv) A car burning petrol.',
        steps: [
          { step: 1, instruction: 'Tree', working: 'Photosynthesis — REMOVES CO₂' },
          { step: 2, instruction: 'Mouse', working: 'Respiration — ADDS CO₂' },
          { step: 3, instruction: 'Fungus', working: 'Decomposition — ADDS CO₂' },
          { step: 4, instruction: 'Car', working: 'Combustion — ADDS CO₂' },
        ],
        answer: '1 removes; 3 add. Three of the four processes increase atmospheric CO₂.',
        commonMistake: 'Forgetting that decomposition adds CO₂ — fungi and bacteria respire too.',
      },
    ],
    whyItWorks:
      "Carbon is element #6, the backbone of every living thing. It cycles between rocks, air, plants, animals and oceans. As long as the cycle stays balanced, climate stays stable. Rapidly burning fossil fuels tips the balance — and Earth's climate notices.",
  },
  glossary: [
    { term: 'Photosynthesis', definition: 'CO₂ + water + sunlight → glucose + oxygen (plants only).' },
    { term: 'Respiration', definition: 'Glucose + oxygen → CO₂ + water + energy.' },
    { term: 'Combustion', definition: 'Burning a fuel — releases CO₂ if carbon-containing.' },
    { term: 'Decomposition', definition: 'Breakdown of dead organisms by fungi/bacteria — releases CO₂.' },
    { term: 'Fossil fuel', definition: 'Coal, oil or natural gas — dead remains buried for millions of years.' },
    { term: 'Carbon sink', definition: 'A long-term store of carbon (e.g. forests, oceans, limestone).' },
  ],
  examQuestion: {
    prompt:
      '(a) Name TWO processes that REMOVE CO₂ from the atmosphere AND TWO that release it. (b) Explain why burning fossil fuels has raised atmospheric CO₂. (c) Suggest TWO ways humans could reduce CO₂ released. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Removes: photosynthesis (and any 1 of: dissolving in oceans / rock formation in shells)',
      'Adds: respiration, combustion, decomposition (any 2)',
      'Fossil fuels were stored for millions of years; burning them releases CO₂ much faster than the cycle can absorb',
      'Plant trees / use renewable energy / reduce car use / improve building insulation (any 2 sensible)',
      "(Don't accept 'recycle paper' alone — clarify must reduce burning of fossils)",
    ],
  },
  practice: {
    warmUp: [
      { id: 'chem-7.5.w1', type: 'text', question: 'Plants use ___ to absorb CO₂ from the air.', answer: 'photosynthesis', hint1: '14 letters', hint2: 'Sun-driven', hint3: 'Starts with p' },
      { id: 'chem-7.5.w2', type: 'choice', question: 'Burning coal is an example of…', options: ['Photosynthesis', 'Combustion', 'Respiration', 'Decomposition'], answer: 'Combustion', hint1: 'Burning a fuel', hint2: 'Releases CO₂', hint3: '10 letters' },
      { id: 'chem-7.5.w3', type: 'choice', question: 'Long-term store of carbon in shells/rock?', options: ['Sandstone', 'Limestone', 'Slate', 'Marble'], answer: 'Limestone', hint1: 'Calcium carbonate', hint2: '9 letters', hint3: 'Sedimentary' },
    ],
    level1: [
      { id: 'chem-7.5.1', type: 'choice', question: 'Which process RELEASES CO₂?', options: ['Photosynthesis', 'Respiration', 'Sunbathing', 'Filtration'], answer: 'Respiration', hint1: 'Energy from glucose', hint2: 'You do it 24/7', hint3: 'Living things' },
      { id: 'chem-7.5.2', type: 'text', question: 'Coal, oil and gas are examples of ___ fuels.', answer: 'fossil', hint1: '6 letters', hint2: 'Buried for millions of years', hint3: 'Starts with f' },
    ],
    level2: [
      { id: 'chem-7.5.3', type: 'choice', question: 'Cutting down forests increases atmospheric CO₂ because…', options: ['Trees produce CO₂', 'Fewer trees do photosynthesis', 'Wood burns', 'Both B and C'], answer: 'Both B and C', hint1: 'Two reasons', hint2: 'Fewer absorbers + extra burning', hint3: 'Combined effect' },
      { id: 'chem-7.5.4', type: 'choice', question: 'A "carbon sink" is…', options: ['A factory chimney', 'A long-term store of carbon', 'A landfill', 'A volcano'], answer: 'A long-term store of carbon', hint1: 'Forest, ocean, limestone', hint2: 'Holds carbon for ages', hint3: 'Storage' },
    ],
    level3: [
      { id: 'chem-7.5.5', type: 'choice', question: 'Which of these is NOT part of the natural carbon cycle?', options: ['Photosynthesis', 'Respiration', 'Decomposition', 'Burning fossil fuels at scale'], answer: 'Burning fossil fuels at scale', hint1: 'Industrial scale is recent', hint2: 'Tilts the cycle', hint3: 'Human-caused' },
      { id: 'chem-7.5.6', type: 'text', question: 'Breakdown of dead organisms by fungi and bacteria is called ___ .', answer: 'decomposition', hint1: '13 letters', hint2: 'Rotting', hint3: 'Releases CO₂' },
    ],
  },
};

// =====================================================================
// 7.6  THE ATMOSPHERE AND THE CLIMATE
// =====================================================================
const ATMOSPHERE_TOPIC = {
  id: 'chem-7.6',
  title: 'The Atmosphere and the Climate',
  slug: 'atmosphere-and-climate',
  pageRef: 'Section 7 · Topic 6',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'What\u2019s in the air?',
        body:
          'Earth\u2019s atmosphere today is about 78% nitrogen (N₂), 21% oxygen (O₂), and 1% argon and other gases — including a small but crucial 0.04% carbon dioxide and small amounts of water vapour. We need oxygen for respiration; plants need CO₂ for photosynthesis; nitrogen mostly just sits there but is essential in proteins (recycled by bacteria).',
        check: {
          question: 'What is the most common gas in air?',
          options: ['Oxygen', 'Carbon dioxide', 'Nitrogen', 'Hydrogen'],
          answer: 'Nitrogen',
          explainer: 'About 78% of the air is N₂ — far more than oxygen.',
        },
      },
      {
        heading: 'The greenhouse effect',
        body:
          "Some gases — CO₂, methane, water vapour — let sunlight in but trap heat radiated back from Earth's surface. Without this greenhouse effect, Earth would be around -18 °C — frozen. The natural greenhouse effect has kept the planet a comfortable ~15 °C average. The problem is too much: extra greenhouse gases trap MORE heat, making the planet warmer.",
        check: {
          question: 'The greenhouse effect helps Earth by…',
          options: ['Cooling it down', 'Keeping it warm enough for life', 'Producing oxygen', 'Removing CO₂'],
          answer: 'Keeping it warm enough for life',
          explainer: "Without it, Earth would be a frozen ball. We need SOME greenhouse effect — just not too much.",
        },
      },
      {
        heading: 'Climate change',
        body:
          'Burning fossil fuels and deforestation have pushed atmospheric CO₂ from ~280 ppm before 1850 to over 420 ppm today. That extra CO₂ traps extra heat. Effects: rising global temperatures, melting ice caps, rising sea levels, more extreme weather, ocean acidification (CO₂ dissolves into oceans → carbonic acid → harms shells/coral). Solutions: switch to renewables (wind, solar), plant trees, use less energy, reduce meat consumption, electrify transport.',
        check: {
          question: 'Which of these is NOT a likely effect of climate change?',
          options: ['Sea levels rising', 'More extreme weather', 'Ice caps melting', 'Stronger gravity'],
          answer: 'Stronger gravity',
          explainer: 'Gravity is set by mass, not climate. The other three are well-documented effects.',
        },
      },
    ],
    keyPoints: [
      'Air today: ~78% N₂, ~21% O₂, ~1% argon + traces (CO₂ ~0.04%)',
      'Greenhouse gases (CO₂, methane, water vapour) trap heat',
      'Natural greenhouse effect = good (Earth warm enough for life)',
      'Too many GHGs = global warming + climate change',
      'Effects: hotter, ice melt, sea-level rise, extreme weather, ocean acidification',
      'Solutions: renewables, trees, energy efficiency, less meat',
    ],
    workedExamples: [
      {
        problem: 'A student says: "If the natural greenhouse effect keeps Earth warm, why is climate change a problem?"',
        steps: [
          { step: 1, instruction: 'Set the baseline', working: 'A natural amount of CO₂/methane keeps Earth at ~15 °C — perfect for life' },
          { step: 2, instruction: 'Identify the change', working: 'Humans have raised CO₂ from 280 to over 420 ppm in less than 200 years' },
          { step: 3, instruction: 'Consequence', working: 'Extra GHGs trap extra heat → average temperature rises → ecosystems and weather destabilise' },
        ],
        answer: 'A small natural greenhouse effect is essential. The problem is the EXTRA, human-caused warming on top — which the planet\u2019s ecosystems can\u2019t adjust to fast enough.',
        commonMistake: 'Saying the greenhouse effect itself is bad. It isn\u2019t — too much of it is.',
      },
    ],
    whyItWorks:
      'Earth is a finely tuned chemistry experiment. Tiny changes in atmospheric composition cause big changes in temperature. The good news: changes we cause we can also undo, by reducing greenhouse-gas emissions and absorbing CO₂ back through forests, soils and tech.',
  },
  glossary: [
    { term: 'Atmosphere', definition: 'The layer of gases surrounding Earth.' },
    { term: 'Greenhouse effect', definition: 'Trapping of heat by gases in the atmosphere.' },
    { term: 'Greenhouse gas', definition: 'A gas (e.g. CO₂, methane, water vapour) that traps heat.' },
    { term: 'Climate change', definition: 'Long-term changes in temperature and weather patterns, mostly caused by human emissions.' },
    { term: 'Renewable energy', definition: 'Energy from sources that won\u2019t run out (wind, solar, hydro, geothermal).' },
    { term: 'Ocean acidification', definition: 'Falling pH of oceans as CO₂ dissolves and forms carbonic acid.' },
  ],
  examQuestion: {
    prompt:
      "(a) State the approximate percentage of nitrogen and oxygen in air today. (b) Explain how greenhouse gases keep Earth warm. (c) Suggest TWO ways the UK could cut its CO₂ emissions. (6 marks)",
    maxMarks: 6,
    markSchemePoints: [
      '~78% nitrogen, ~21% oxygen',
      'Greenhouse gases let sunlight reach the surface',
      'Earth re-emits heat (infrared) which is absorbed by GHGs',
      'GHGs re-emit it in all directions — keeping the surface warmer',
      'Switch to renewable energy (wind/solar/hydro)',
      'Insulate homes / electrify transport / plant trees / reduce flying (any sensible 2nd)',
    ],
  },
  practice: {
    warmUp: [
      { id: 'chem-7.6.w1', type: 'number', question: 'Approx % nitrogen in the air?', answer: '78', hint1: 'Two digits', hint2: 'Most of the air', hint3: 'Around 4/5' },
      { id: 'chem-7.6.w2', type: 'number', question: 'Approx % oxygen in the air?', answer: '21', hint1: 'Two digits', hint2: '~1/5 of air', hint3: 'Just over 20' },
      { id: 'chem-7.6.w3', type: 'choice', question: 'Which is a greenhouse gas?', options: ['Nitrogen', 'Oxygen', 'Methane', 'Argon'], answer: 'Methane', hint1: 'Cattle burps', hint2: 'CH₄', hint3: '7 letters' },
    ],
    level1: [
      { id: 'chem-7.6.1', type: 'choice', question: 'The natural greenhouse effect is…', options: ['Bad — overheats Earth', 'Good — keeps Earth warm enough for life', 'Made by humans', 'Has no effect'], answer: 'Good — keeps Earth warm enough for life', hint1: 'Without it, -18 °C', hint2: 'Essential for life', hint3: 'Maintains 15 °C' },
      { id: 'chem-7.6.2', type: 'text', question: 'A long-term change in temperature and weather patterns is called ___ change.', answer: 'climate', hint1: '7 letters', hint2: 'Topic title bit', hint3: 'Starts with c' },
    ],
    level2: [
      { id: 'chem-7.6.3', type: 'choice', question: 'Why is ocean acidification happening?', options: ['Salt is increasing', 'CO₂ dissolving in oceans makes carbonic acid', 'Plastic in water', 'Higher salinity'], answer: 'CO₂ dissolving in oceans makes carbonic acid', hint1: 'Linked to atmospheric CO₂', hint2: 'pH falls', hint3: 'Damages shells/coral' },
      { id: 'chem-7.6.4', type: 'choice', question: 'Which is a renewable energy source?', options: ['Coal', 'Oil', 'Solar', 'Natural gas'], answer: 'Solar', hint1: 'From the Sun', hint2: 'Doesn\u2019t run out', hint3: 'Photovoltaic' },
    ],
    level3: [
      { id: 'chem-7.6.5', type: 'choice', question: "Pre-1850 CO₂ was about 280 ppm. Today it is over…", options: ['100 ppm', '200 ppm', '300 ppm', '420 ppm'], answer: '420 ppm', hint1: 'Higher than ever in 800,000 years', hint2: 'Three digits', hint3: 'Above 400' },
      { id: 'chem-7.6.6', type: 'choice', question: 'Which is the BEST single action to reduce a country\u2019s CO₂ emissions?', options: ['Recycle more paper', 'Replace fossil-fuel power stations with renewables', 'Use disposable cups', 'Drive faster'], answer: 'Replace fossil-fuel power stations with renewables', hint1: 'Biggest source = electricity', hint2: 'Big lever', hint3: 'Decarbonise the grid' },
    ],
  },
};

// ---------- assemble the section ----------
const BASE_TOPICS = [
  STRUCTURE_TOPIC,
  ROCK_TYPES_TOPIC,
  ROCK_CYCLE_TOPIC,
  RECYCLING_TOPIC,
  CARBON_CYCLE_TOPIC,
  ATMOSPHERE_TOPIC,
];

// authoredTopics.json overrides any topic by id (for the PDF pipeline).
export const earthAndAtmosphereTopics = BASE_TOPICS.map((t) =>
  AUTHORED_BY_ID[t.id] ? AUTHORED_BY_ID[t.id] : t,
);
